import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Order, getObjectId } from '@hero/schema';
import type { OrderLocationChangePreview, OrderUpdateAPI } from '@hero/api';
import { useOrderLocationChangePreviewApi, useOrderUpdateApi } from '@hero/api-hooks';
import { isInternalShipment, useViewerLeg } from '@hero/web-hooks';
import type { MapLocationData } from '@hero/web-components';
import { OrderRouteCompact } from './OrderRouteCompact';
import {
  LocationChangePriceModal,
  type LocationChangePriceChoice,
} from './LocationChangePriceModal';
import { OrderMapModal } from '../Map';

export interface OrderRouteWithMapProps {
  order: Order;
  vehicle?: Order['load']['vehicles'][0];
}

type APIDestinations = NonNullable<OrderUpdateAPI['req']['destinations']>;

/**
 * Identity of a candidate route, used to tell whether a cached preview
 * still describes the pins the user is about to save. Only the fields the
 * preview's answer depends on — where each stop is and which saved place
 * it points at.
 */
const routeSignature = (destinations: APIDestinations): string =>
  JSON.stringify(
    destinations.map(d => [d.type, d.location, d.savedLocationId?.toString() ?? null]),
  );

/**
 * Shared "click route → open map" wrapper for any web dashboard.
 *
 * Hosts that want the "Route" column in an `OrderTableManager` to be
 * clickable (and to open `OrderMapModal` so users can both view AND
 * edit the order's pickup / dropoff destinations) plug this in via a
 * column `render`:
 *
 * ```tsx
 * <OrderTableManager
 *   columns={[
 *     ...,
 *     {
 *       key: 'destinations',
 *       render: order => (
 *         <OrderRouteWithMap
 *           order={order}
 *           vehicle={order.load?.vehicles?.[0]}
 *         />
 *       ),
 *     },
 *   ]}
 * />
 * ```
 *
 * It intentionally lives in `libs/frontend/web/components` rather than
 * each app's local `features/Orders/` folder. The former per-app
 * `OrderRouteViewWithMap` / `OrderRouteDisplayWithMap` variants were
 * near-duplicate forks of the same code — both wrapped `OrderRouteDisplay` +
 * `OrderMapModal`, both translated `MapLocationData[]` into
 * `OrderUpdateAPI['req']['destinations']`, and both routed the PATCH
 * through their own per-app `useUpdateOrder` hook. Now that the
 * tenant-aware `useOrderUpdateApi` (`@hero/api-hooks`) is the canonical
 * order-update mutation everywhere, the shared component depends on
 * it directly so non-app surfaces — most importantly the CRM
 * `ClientWorkspacePage` Orders tab, which lives in this same lib —
 * can finally render a clickable Route cell without reaching back
 * into a specific app.
 *
 * Behavior:
 * - Route cell is rendered via `OrderRouteCompact` with both `order`
 *   and `onClick` set, so it gets the `cursor-pointer` + hover state
 *   and stops propagation on click (no bleed into row-level handlers).
 * - On click, `OrderMapModal` opens with `orgId` derived from the
 *   order's shipper for saved-location scoping.
 * - On save, `MapLocationData[]` is translated back into the canonical
 *   `OrderUpdateAPI['req']['destinations']` shape (preserving the
 *   destination's existing `instructions` and falling back to existing
 *   contacts when the user didn't pick a new saved location), and the
 *   order is patched via `useOrderUpdateApi`. The modal stays open
 *   while `isPending` is true and only closes on success.
 */
export const OrderRouteWithMap: React.FC<OrderRouteWithMapProps> = ({ order }) => {
  const { t } = useTranslation('m-order');
  const [isMapOpen, setIsMapOpen] = useState(false);
  const { mutateAsync: updateOrder, isPending } = useOrderUpdateApi();
  const { mutateAsync: previewLocationChange, isPending: isPreviewPending } =
    useOrderLocationChangePreviewApi();
  const viewerLeg = useViewerLeg(order);
  // Set only for a cross-city change that needs the user to pick a price;
  // holds the destinations so confirming doesn't re-derive them.
  const [pendingCrossCity, setPendingCrossCity] = useState<{
    preview: OrderLocationChangePreview;
    destinations: APIDestinations;
  } | null>(null);
  // Preview fetched the moment the pins settled, so Save doesn't have to
  // wait on the network. Keyed by `routeSignature` so a stale answer from
  // an earlier pin position is never reused.
  const [cachedPreview, setCachedPreview] = useState<{
    signature: string;
    preview: OrderLocationChangePreview;
  } | null>(null);
  const latestSignature = useRef<string>('');

  const handleClick = useCallback(() => {
    setCachedPreview(null);
    latestSignature.current = '';
    setIsMapOpen(true);
  }, []);

  // `MapLocationData` (UI shape) → `OrderUpdateAPI['req']['destinations']`
  // (server shape). Mirrors the per-app converters that used to live in
  // `apps/*-dashboard/.../OrderRoute*WithMap.tsx`. The conversion
  // intentionally preserves fields the map UI doesn't surface
  // (`instructions`, existing destination contacts) so a save that only
  // moves a pin doesn't silently nuke metadata.
  const convertToAPIDestinations = useCallback(
    (locations: MapLocationData[]): APIDestinations => {
      return locations.map((location, index) => {
        const destinationType = location.data?.type || (index === 0 ? 'pickup' : 'dropoff');
        const existingDestination = order.destinations?.find(d => d.type === destinationType);

        const savedLocationId =
          location.savedLocation?._id || location.savedLocationId
            ? getObjectId(
                location.savedLocation?._id?.toString() || location.savedLocationId,
              )
            : undefined;

        const thirdPartyClientId = location.savedLocation?.client?._id
          ? getObjectId(location.savedLocation.client._id.toString())
          : undefined;

        const isAccurateLocation =
          location.isAccurateLocation !== undefined
            ? location.isAccurateLocation
            : !!location.savedLocation;

        // A link describes a pin, so it only survives a save that leaves
        // the pin where it was. Carrying it onto a moved stop would open
        // Maps at the place the truck is no longer going to.
        const previousCords = existingDestination?.address?.location?.coordinates;
        const stayedPut =
          previousCords?.[1] === location.cords?.lat &&
          previousCords?.[0] === location.cords?.lng;

        return {
          type: destinationType,
          location: location.cords,
          isAccurateLocation,
          locationUrl:
            location.locationUrl ?? (stayedPut ? existingDestination?.locationUrl : undefined),
          savedLocationId,
          customerId: thirdPartyClientId,
          instructions: existingDestination?.instructions,
          contacts: location.savedLocation?.contacts?.length
            ? location.savedLocation.contacts.map(c => ({
                _id: c._id ? getObjectId(c._id.toString()) : undefined,
                phone: c.phone || '',
                name: c.name || '',
              }))
            : existingDestination?.contacts?.length
            ? existingDestination.contacts.map(c => ({
                _id: c._id ? getObjectId(c._id.toString()) : undefined,
                phone: c.phone || '',
                name: c.name || '',
              }))
            : undefined,
        };
      });
    },
    [order.destinations],
  );

  /**
   * Quote the candidate route, discarding the answer if the pins have moved
   * on since the request went out.
   *
   * Returns `undefined` on failure: the preview is advisory, and refusing
   * to save because a quote couldn't be fetched would make destinations
   * un-editable during a Google or resolver outage.
   */
  const runPreview = useCallback(
    async (destinations: APIDestinations) => {
      const signature = routeSignature(destinations);
      latestSignature.current = signature;

      const preview = await previewLocationChange({
        _id: order._id,
        destinations,
      }).catch(() => undefined);

      // A newer edit was finalized while this was in flight — its own
      // preview owns the cache.
      if (latestSignature.current !== signature) return undefined;

      setCachedPreview(preview ? { signature, preview } : null);
      return preview;
    },
    [order._id, previewLocationChange],
  );

  /**
   * Quote as soon as the pins settle (the green check on a stop), not when
   * Save is clicked. By the time the user reaches Save the answer is
   * usually already in hand, so a cross-city change shows its price
   * dialog immediately instead of after a spinner.
   */
  const handleLocationsFinalized = useCallback(
    (locations: MapLocationData[]) => {
      if (locations.length < 2) return;
      void runPreview(convertToAPIDestinations(locations));
    },
    [convertToAPIDestinations, runPreview],
  );

  /**
   * Preview first, PATCH second.
   *
   * A same-city move (the common case — dragging a pin to the right gate)
   * saves straight through; asking the user to confirm a price that isn't
   * changing would be noise. A cross-city move opens
   * `LocationChangePriceModal` so the price decision is explicit, and a
   * blocked one surfaces the backend's reason as a toast rather than
   * letting the PATCH fail after the modal has already closed.
   *
   * `handleLocationsFinalized` has normally already fetched this exact
   * quote, so the cache hit is the common path; the re-fetch covers a save
   * whose pins never went through a per-stop edit (e.g. a marker drag) and
   * an earlier preview that failed.
   */
  const handleLocationSave = useCallback(
    async (locations: MapLocationData[]) => {
      if (locations.length < 2) return false;
      const destinations = convertToAPIDestinations(locations);
      const signature = routeSignature(destinations);

      const preview =
        cachedPreview?.signature === signature
          ? cachedPreview.preview
          : await runPreview(destinations);

      if (preview?.blockedReason) {
        toast.error(
          t(`location_change.blocked.${preview.blockedReason}`, {
            defaultValue: t(
              'location_change.blocked.generic',
              "This order's destinations can't be changed.",
            ),
          }),
        );
        // Stay on the map so the pins that caused this are still visible
        // and the user can put them back.
        return false;
      }

      if (preview?.crossCity) {
        // Same gate as Edit Price (`canEditPrice`): need a viewer leg
        // on a non-internal order. Contracts alone aren't enough — the
        // JWT org must be a party, or the price PATCH 403s.
        const pricingAllowed = Boolean(viewerLeg) && !isInternalShipment(order);
        if (!pricingAllowed) {
          await updateOrder({
            _id: order._id,
            destinations,
            locationChangePricing: 'keep_previous',
          });
          setIsMapOpen(false);
          return true;
        }
        setPendingCrossCity({ preview, destinations });
        // The price modal owns the rest of this save, including closing
        // the map once it lands.
        return false;
      }

      await updateOrder({ _id: order._id, destinations });
      setIsMapOpen(false);
      return true;
    },
    [
      cachedPreview,
      convertToAPIDestinations,
      order,
      runPreview,
      t,
      updateOrder,
      viewerLeg,
    ],
  );

  /**
   * Commit the cross-city change with the price the user picked.
   *
   * A custom price goes through `locationChangePricing: 'custom'` rather
   * than a raw `beneficiaryAmount` write so the backend keeps ownership of
   * fee / tax / insurance recomputation — the same reason `resolve_route`
   * doesn't assemble an Amount client-side.
   */
  const handleCrossCityConfirm = useCallback(
    async (choice: LocationChangePriceChoice) => {
      if (!pendingCrossCity) return;
      const pricing: Pick<
        OrderUpdateAPI['req'],
        'locationChangePricing' | 'locationChangePrice'
      > =
        choice.mode === 'route'
          ? { locationChangePricing: 'resolve_route' }
          : { locationChangePricing: 'custom', locationChangePrice: choice.price };

      await updateOrder({
        _id: order._id,
        destinations: pendingCrossCity.destinations,
        ...pricing,
      });
      setPendingCrossCity(null);
      setIsMapOpen(false);
    },
    [order._id, pendingCrossCity, updateOrder],
  );

  return (
    <>
      <OrderRouteCompact destinations={order.destinations} order={order} onClick={handleClick} />
      <OrderMapModal
        open={isMapOpen}
        onOpenChange={setIsMapOpen}
        order={order}
        onLocationSave={handleLocationSave}
        onLocationsFinalized={handleLocationsFinalized}
        saveInProgress={isPending}
        savePreparing={isPreviewPending}
        savePreparingReason="price_check"
        orgId={order.shipper?._id?.toString()}
      />
      {pendingCrossCity ? (
        <LocationChangePriceModal
          open
          onOpenChange={open => {
            if (!open) setPendingCrossCity(null);
          }}
          preview={pendingCrossCity.preview}
          onConfirm={handleCrossCityConfirm}
          isSaving={isPending}
        />
      ) : null}
    </>
  );
};
